/**
 * Pure sound decisions for the animated card battlefield.
 *
 * The server sends semantic beats and the animator owns their timing. This
 * module only maps card/effect identity to the shared SoundEvent vocabulary so
 * playback remains governed by AudioManager's existing mute, volume, residency,
 * and graceful-missing-asset behaviour.
 */
import type {
  WireCardBattleEffectBeat,
  WireCardRarity,
} from '../net/protocol';
import { cardDef, isKnownCard } from '../sim/cardContent';
import type { MonsterSoundRole } from '../sim/content';
import type { SoundEvent } from './soundObserver';
import { monsterCue, spellCue } from './combatCue';
import { cardPresentationProfile } from '../cardBattlePresentation';

export const CARD_ATTACK_VOLUME = 0.7;
export const CARD_REACTION_VOLUME = 0.62;
export const CARD_DEATH_VOLUME = 0.78;
export const CARD_SPELL_VOLUME = 0.82;

/** The identity retained for a minion after authoritative state removes it. */
export interface CardBattleSoundIdentity {
  cardId: string;
  rarity: WireCardRarity;
}

/** Resolve one bestiary voice for a monster card, degrading silently for unknown/non-monster cards. */
export function cardMonsterCue(
  identity: CardBattleSoundIdentity | undefined,
  role: MonsterSoundRole,
  volume = role === 'die' ? CARD_DEATH_VOLUME : CARD_REACTION_VOLUME,
): SoundEvent | null {
  if (!identity || !isKnownCard(identity.cardId)) return null;
  const def = cardDef(identity.cardId, identity.rarity);
  return def.kind === 'monster' ? monsterCue(def.body, role, volume) : null;
}

/**
 * One signature sound for an engine effect. The animator de-duplicates this
 * within an accepted action so Earthquake/Chain Lightning sound once rather
 * than once per affected target.
 */
export function cardEffectCue(beat: WireCardBattleEffectBeat): SoundEvent | null {
  const profile = cardPresentationProfile(beat.sourceCardId);
  if (beat.effect === 'damage') {
    const effect = profile?.projectile ?? profile?.impact;
    return effect ? spellCue(effect, CARD_SPELL_VOLUME) : null;
  }
  if (beat.effect === 'heal') {
    return spellCue(profile?.impact ?? 'greater-heal', CARD_SPELL_VOLUME);
  }
  if (beat.effect === 'ward') {
    return spellCue(profile?.impact ?? 'greater-heal', CARD_SPELL_VOLUME);
  }
  if (beat.effect === 'status' && beat.action === 'apply') {
    const effect = profile?.impact;
    return effect ? spellCue(effect, CARD_SPELL_VOLUME) : null;
  }
  if (beat.effect === 'delayed-damage' && beat.action === 'detonate') {
    return spellCue('explosion', CARD_SPELL_VOLUME);
  }
  if (beat.effect === 'return-to-hand') {
    return spellCue('poof', CARD_SPELL_VOLUME);
  }
  if (beat.effect === 'summon') {
    return null;
  }
  if (beat.effect === 'fizzle') {
    return { sound: 'sphere-fizzle', volume: CARD_SPELL_VOLUME };
  }
  return null;
}
